import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from './components/Loader';
import React from 'react';
import { MemberListTile } from './components/MemberListTile';
import { useAuth } from '../../hooks/useAuth';
import { LayoutWithTopBar } from '../../components/LayoutWithTopBar';
import { formatAmount } from '../../utils/Utils';
import { useRouter } from '../../hooks/useRouter';
import { canManageMembers, Pleb } from '../../models/Pleb';
import { Event } from '../../models/Event';
import { useInView } from 'react-intersection-observer';
import { eventsRepository } from '../../repositories/EventsRepository';
import { ChatChannelType } from '../../models/ChatChannel';

interface PageEventParticipantsProps {}

const MEMBERS_LIMIT = 50;

const PageEventParticipants: React.FC<PageEventParticipantsProps> = ({}) => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [event, setEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMembersLoading, setIsMembersLoading] = useState(false);
  const [hasMoreMembers, setHasMoreMembers] = useState(true);
  const [members, setMembers] = useState<Pleb[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const {pleb} = useAuth();
  const {navigateToEvent, navigateToProfile} = useRouter();
  const { ref, inView } = useInView();
  const pageRef = useRef(0);

  useEffect(() => {
    setIsLoading(true);

    eventsRepository.getEventDetails(eventId ?? '', pleb?.id ?? '')
      .then((event) => {
        setEvent(event);
        setError(null);
      })
      .catch((err) => {
        console.error('Error getting event:', err);
        setError(err as Error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [eventId]);

  useEffect(() => {
    if(inView && hasMoreMembers && !isMembersLoading) {
      setIsMembersLoading(true);
      eventsRepository.getParticipants(eventId ?? '', pleb?.id ?? '', pageRef.current, MEMBERS_LIMIT)
        .then(({members, hasMore}) => {
          pageRef.current += 1;
          setMembers(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newMembers = members.filter(m => !existingIds.has(m.id));
            return [...prev, ...newMembers];
          });
          setHasMoreMembers(hasMore);
        })
        .catch((err) => {
          setHasMoreMembers(false);
        })
        .finally(() => {
          setIsMembersLoading(false);
        });
    }
  }, [inView, hasMoreMembers, eventId, pleb?.id]);

  const renderEvent = () => {
    if (isLoading) return <Loader />;
    if (error) return <div>Error loading event details</div>;
    if (!event) return null;

    return (
      <div className="p-4 flex flex-col">
        <div className="flex flex-col items-center mb-6">
          <img 
            src={event.image}
            className={`object-cover w-32 h-32 rounded-full bg-white cursor-pointer`}
            alt={event.name}
            onClick={() => {
              navigateToEvent(event.id);
            }}
          />

          <h1 
            className={`mt-2 text-xl font-bold text-center cursor-pointer`}
            onClick={() => {
              navigateToEvent(event.id);
            }}
          >
            {event.name}
          </h1>
          
          <div className="flex flex-row justify-between items-center w-full px-6 mt-3">
            <p className="text-lg">
              {
                event.rsvpCount > 0 && `${formatAmount(event.rsvpCount)} participant${event.rsvpCount > 1 ? 's' : ''}`
              }
            </p>
          </div>
        </div>

        <div className="flex flex-col">
          {
            members.map(member => (
              <MemberListTile
                key={member.id}
                member={member}
                admin={false}
                onTap={() => navigateToProfile(ChatChannelType.Messaging, member.id)}
                allowPressAwaitingReply={(pleb && canManageMembers(pleb)) ? true : false}
              />
            ))
          }
          {isMembersLoading && <div className="flex justify-center items-center py-4"><Loader /></div>}
          {hasMoreMembers && <div ref={ref} className="h-5" />}
        </div>
      </div>
    );
  };


  return (
    <LayoutWithTopBar
      onClickPrevArrow={() => navigate(-1)}
      topBarChildren={
        <div className="flex items-center">
          <h1 className="text-l font-bold text-center">{event?.name}</h1>
        </div>
      }
    >
      {renderEvent()}
    </LayoutWithTopBar>
  );
}

export default PageEventParticipants;